import React, { useState, useEffect, useRef, useCallback } from "react";
import PortalLayout from "@/components/PortalLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/context/WebSocketContext";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import {
  Plus, Paperclip, X, Send, Users, Lock, LinkIcon, ChevronRight, Trash2, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RichTextEditor, { RichTextDisplay } from "@/components/RichTextEditor";

const fmt = (d) => {
  if (!d) return "";
  const diff = Date.now() - new Date(d).getTime();
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return new Date(d).toLocaleDateString();
};

function NewThreadDialog({ onClose, onCreated }) {
  const [subject, setSubject] = useState("");
  const [requestId, setRequestId] = useState("");
  const [requests, setRequests] = useState([]);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/portal/requests-for-threads").then((r) => setRequests(r.data)).catch(() => {});
  }, []);

  const pickRequest = (req) => {
    setRequestId(req.id);
    if (!subject) setSubject(req.title);
  };

  const handleFile = (e) => {
    Array.from(e.target.files || []).forEach((file) => {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} too large (max 10 MB)`); return; }
      const r = new FileReader();
      r.onload = (ev) => {
        const b64 = ev.target.result.split(",")[1];
        setAttachments((p) => [...p, { filename: file.name, content_type: file.type, data_b64: b64 }]);
      };
      r.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    setSaving(true);
    try {
      const t = await api.post("/portal/threads", {
        subject: subject.trim(),
        related_request_id: requestId || null,
        is_org_wide: !!requestId,
        initial_message: message || null,
        initial_attachments: attachments,
      });
      onCreated(t.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create thread");
    } finally {
      setSaving(false);
    }
  };

  const isOrgWide = !!requestId;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.4)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6" data-testid="new-thread-dialog">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-xl font-bold" style={{ color: "#292524" }}>New Conversation</h2>
          <button onClick={onClose} style={{ color: "#A8A29E" }}><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>
              Subject *
            </Label>
            <Input
              data-testid="thread-subject-input"
              autoFocus
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What's this about?"
              className="mt-1.5"
            />
          </div>

          {requests.length > 0 && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#57534E" }}>
                Link to a Request (optional)
              </Label>
              <select
                data-testid="thread-request-select"
                value={requestId}
                onChange={(e) => {
                  const req = requests.find((r) => r.id === e.target.value);
                  if (req) pickRequest(req);
                  else { setRequestId(""); }
                }}
                className="mt-1.5 w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-1 focus:ring-amber-300"
                style={{ borderColor: "#E7E5E4" }}
              >
                <option value="">None — private conversation</option>
                {requests.map((r) => (
                  <option key={r.id} value={r.id}>{r.title}</option>
                ))}
              </select>
              {isOrgWide && (
                <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg" style={{ background: "#FEF3C7" }}>
                  <Users size={13} style={{ color: "#D97706", flexShrink: 0, marginTop: "1px" }} />
                  <p className="text-xs" style={{ color: "#D97706" }}>
                    This conversation is linked to a request and will be <strong>visible to your whole team</strong>.
                  </p>
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: "#57534E" }}>
              Opening Message (optional)
            </Label>
            <RichTextEditor
              value={message}
              onChange={setMessage}
              placeholder="Start the conversation..."
              minHeight={80}
            />
          </div>

          {attachments.length > 0 && (
            <div className="space-y-1">
              {attachments.map((att, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: "#F5F3EF" }}>
                  <span className="text-xs truncate mr-2" style={{ color: "#292524" }}>{att.filename}</span>
                  <button type="button" onClick={() => setAttachments((p) => p.filter((_, j) => j !== i))} style={{ color: "#A8A29E" }}><X size={12} /></button>
                </div>
              ))}
            </div>
          )}

          <input ref={fileRef} type="file" multiple className="hidden" onChange={handleFile} />

          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors hover:bg-stone-50"
              style={{ borderColor: "#E7E5E4", color: "#57534E" }}
            >
              <Paperclip size={12} />Attach files
            </button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
              <Button
                data-testid="create-thread-button"
                type="submit"
                size="sm"
                disabled={saving}
                style={{ background: "#D97706", color: "#fff" }}
              >
                {saving ? "Creating..." : "Create"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function ThreadConversation({ thread, onBack }) {
  const { user } = useAuth();
  const { subscribe, send: wsSend } = useWebSocket();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastTypeSentRef = useRef(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/portal/threads/${thread.id}/messages`)
      .then((r) => setMessages(r.data))
      .finally(() => setLoading(false));
  }, [thread.id]);

  // Subscribe to WS — refresh messages when agency sends to this thread
  useEffect(() => {
    const unsub = subscribe((event) => {
      if (event.type === "new_message" && event.thread_id === thread.id && event.sender_type === "agency") {
        api.get(`/portal/threads/${thread.id}/messages`).then((r) => setMessages(r.data)).catch(() => {});
      }
      if (event.type === "typing" && event.thread_id === thread.id && event.user_id !== user?.id) {
        setTypingUser(event.user_name);
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setTypingUser(null), 3000);
      }
    });
    return unsub;
  }, [subscribe, thread.id, user?.id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleFile = (e) => {
    Array.from(e.target.files || []).forEach((file) => {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} too large (max 10 MB)`); return; }
      const r = new FileReader();
      r.onload = (ev) => {
        const b64 = ev.target.result.split(",")[1];
        setAttachments((p) => [...p, { filename: file.name, content_type: file.type, data_b64: b64 }]);
      };
      r.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const send = async () => {
    if (!content.trim() && attachments.length === 0) return;
    setSending(true);
    try {
      const r = await api.post(`/portal/threads/${thread.id}/messages`, { content, attachments });
      setMessages((p) => [...p, r.data]);
      setContent("");
      setAttachments([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <div className="px-5 py-3 flex items-center gap-3 flex-shrink-0" style={{ borderBottom: "1px solid #E7E5E4", background: "#fff" }}>
        <button onClick={onBack} className="lg:hidden p-1.5 rounded-lg" style={{ color: "#A8A29E" }}>
          <ChevronRight size={16} className="rotate-180" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: "#292524" }}>{thread.subject}</p>
          {thread.related_request_title && (
            <p className="text-xs flex items-center gap-1 mt-0.5" style={{ color: "#A8A29E" }}>
              <LinkIcon size={10} />Re: {thread.related_request_title}
            </p>
          )}
        </div>
        {thread.is_org_wide
          ? <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: "#FEF3C7", color: "#D97706" }}><Users size={10} />Team visible</span>
          : <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: "#F5F3EF", color: "#78716C" }}><Lock size={10} />Private</span>
        }
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {loading ? (
          <p className="text-sm text-center py-8" style={{ color: "#A8A29E" }}>Loading...</p>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full pb-8">
            <p className="text-sm font-medium" style={{ color: "#292524" }}>No messages yet</p>
            <p className="text-xs mt-1" style={{ color: "#A8A29E" }}>Start the conversation below</p>
          </div>
        ) : messages.map((msg) => {
          const isMe = msg.sender_type === "client";
          const initial = msg.sender_name?.[0]?.toUpperCase() || "?";
          return (
            <div key={msg.id} data-testid={`thread-msg-${msg.id}`} className="flex gap-3 items-start">
              <div
                className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-semibold mt-0.5 overflow-hidden"
                style={{
                  background: isMe ? (user?.avatar_image ? "transparent" : (user?.avatar_color || "#D97706")) : "#292524",
                  color: "#fff",
                }}
              >
                {isMe && user?.avatar_image ? <img src={user.avatar_image} alt="" className="w-full h-full object-cover" /> : initial}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-sm font-semibold" style={{ color: "#292524" }}>{msg.sender_name}</span>
                  <span className="text-xs" style={{ color: "#C7C3BF" }}>{fmt(msg.created_at)}</span>
                </div>
                <div className="rounded-xl px-4 py-3" style={{ background: isMe ? "#FAFAF9" : "#fff", border: "1px solid #E7E5E4" }}>
                  <RichTextDisplay html={msg.content} />
                  {msg.attachments?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {msg.attachments.map((att, i) => (
                        <a
                          key={i}
                          href={`data:${att.content_type};base64,${att.data_b64}`}
                          download={att.filename}
                          className="flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-lg hover:bg-stone-50 transition-colors"
                          style={{ color: "#0284C7", border: "1px solid #E7E5E4" }}
                        >
                          <Paperclip size={11} />{att.filename}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {typingUser && (
          <div className="flex items-center gap-2 text-xs" style={{ color: "#A8A29E" }}>
            <div className="flex gap-0.5 items-center">
              <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "#C7C3BF", animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "#C7C3BF", animationDelay: "150ms" }} />
              <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "#C7C3BF", animationDelay: "300ms" }} />
            </div>
            <span>{typingUser} is typing...</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <div className="flex-shrink-0 p-4" style={{ borderTop: "1px solid #E7E5E4", background: "#fff" }}>
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {attachments.map((att, i) => (
              <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs" style={{ background: "#F5F3EF", color: "#292524" }}>
                <Paperclip size={10} />
                <span className="max-w-[120px] truncate">{att.filename}</span>
                <button type="button" onClick={() => setAttachments((p) => p.filter((_, j) => j !== i))} style={{ color: "#A8A29E" }}><X size={10} /></button>
              </div>
            ))}
          </div>
        )}
        <RichTextEditor
          value={content}
          onChange={(v) => {
            setContent(v);
            const now = Date.now();
            if (now - lastTypeSentRef.current > 2000) {
              lastTypeSentRef.current = now;
              wsSend({ type: "typing", thread_id: thread.id, user_name: user?.name || "Client" });
            }
          }}
          placeholder="Write a reply..."
          minHeight={60}
        />
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" multiple className="hidden" onChange={handleFile} />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors hover:bg-stone-50"
              style={{ borderColor: "#E7E5E4", color: "#57534E" }}
              data-testid="attach-in-thread-button"
            >
              <Paperclip size={12} />Attach
            </button>
          </div>
          <Button
            data-testid="send-thread-reply-button"
            size="sm"
            disabled={sending || (!content.trim() && attachments.length === 0)}
            onClick={send}
            style={{ background: "#D97706", color: "#fff" }}
          >
            <Send size={13} className="mr-1" />Reply
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PortalMessages() {
  const { subscribe } = useWebSocket();
  const [searchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [mobileView, setMobileView] = useState("list"); // "list" | "conversation"

  const load = useCallback(() => {
    api.get("/portal/threads")
      .then((r) => {
        setThreads(r.data);
        const deepLinkId = searchParams.get("thread");
        if (r.data.length > 0 && !selected) {
          const target = deepLinkId ? r.data.find((t) => t.id === deepLinkId) : null;
          setSelected(target || r.data[0]);
          if (target) setMobileView("conversation");
        }
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Mark all portal notifications as read when this page is visited
  useEffect(() => {
    api.post("/portal/notifications/mark-all-read").catch(() => {});
  }, []);

  // Subscribe to WS — refresh thread list when agency sends a message
  useEffect(() => {
    const unsub = subscribe((event) => {
      if (event.type === "new_message" && event.sender_type === "agency") {
        api.get("/portal/threads").then((r) => setThreads(r.data)).catch(() => {});
      }
    });
    return unsub;
  }, [subscribe]);

  const onCreated = (t) => {
    setThreads((p) => [t, ...p]);
    setSelected(t);
  };

  const handleDelete = async (threadId) => {
    try {
      await api.delete(`/portal/threads/${threadId}`);
      setThreads((p) => p.filter((t) => t.id !== threadId));
      if (selected?.id === threadId) setSelected(null);
      toast.success("Conversation deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <PortalLayout>
      <div data-testid="portal-messages-page">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="font-serif text-3xl font-bold" style={{ color: "#292524" }}>Messages</h1>
            <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>Conversations with the Balbis team</p>
          </div>
          <Button
            data-testid="new-thread-button"
            size="sm"
            onClick={() => setShowNew(true)}
            style={{ background: "#D97706", color: "#fff" }}
          >
            <Plus size={14} className="mr-1" />New Conversation
          </Button>
        </div>

        <div className="rounded-xl border overflow-hidden flex flex-col md:flex-row" style={{ borderColor: "#E7E5E4", minHeight: "500px" }}>
          {/* Thread list */}
          <div className={`${mobileView === "list" ? "flex" : "hidden"} md:flex w-full md:w-72 flex-shrink-0 flex-col`} style={{ borderRight: "1px solid #E7E5E4" }}>
            <div className="px-4 py-3 flex-shrink-0" style={{ borderBottom: "1px solid #E7E5E4", background: "#FAFAF9" }}>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>Conversations</p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <p className="text-xs text-center py-8" style={{ color: "#A8A29E" }}>Loading...</p>
              ) : threads.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full px-4 pb-8">
                  <p className="text-sm font-medium text-center" style={{ color: "#292524" }}>No conversations yet</p>
                  <p className="text-xs mt-1 text-center" style={{ color: "#A8A29E" }}>Start one with the button above</p>
                </div>
              ) : threads.map((t) => (
                <div
                  key={t.id}
                  className="relative group"
                  style={{ borderBottom: "1px solid #E7E5E4" }}
                >
                  <button
                    data-testid={`thread-item-${t.id}`}
                    onClick={() => { setSelected(t); setMobileView("conversation"); }}
                    className="w-full text-left px-4 py-3 transition-colors pr-10"
                    style={{ background: selected?.id === t.id ? "#F5F3EF" : "transparent" }}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className={`text-sm flex-1 truncate ${t.unread_by_client ? "font-semibold" : "font-medium"}`} style={{ color: "#292524" }}>
                        {t.unread_by_client && <span className="inline-block w-2 h-2 rounded-full mr-1.5 mb-0.5" style={{ background: "#D97706" }} />}
                        {t.subject}
                      </p>
                      <span className="text-xs flex-shrink-0" style={{ color: "#C7C3BF" }}>{fmt(t.last_message_at)}</span>
                    </div>
                    <p className="text-xs truncate" style={{ color: "#A8A29E" }}>
                      {t.last_message_preview || "No messages yet"}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      {t.is_org_wide
                        ? <span className="flex items-center gap-0.5 text-xs" style={{ color: "#D97706" }}><Users size={9} />Team</span>
                        : <span className="flex items-center gap-0.5 text-xs" style={{ color: "#A8A29E" }}><Lock size={9} />Private</span>
                      }
                      {t.related_request_title && (
                        <span className="text-xs truncate" style={{ color: "#A8A29E" }}>· {t.related_request_title}</span>
                      )}
                    </div>
                  </button>
                  {/* Delete button — shown on hover */}
                  {deletingId === t.id ? (
                    <div className="absolute right-2 top-3 flex items-center gap-1 z-10">
                      <button
                        onClick={() => handleDelete(t.id)}
                        className="text-xs px-2 py-1 rounded-lg font-medium"
                        style={{ background: "#FEE2E2", color: "#DC2626" }}
                        data-testid={`confirm-delete-${t.id}`}
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setDeletingId(null)}
                        className="text-xs px-1.5 py-1 rounded-lg"
                        style={{ background: "#F5F3EF", color: "#78716C" }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeletingId(t.id); }}
                      className="absolute right-2 top-3 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ color: "#C7C3BF" }}
                      data-testid={`delete-thread-${t.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Conversation panel */}
          <div className={`${mobileView === "conversation" ? "flex" : "hidden"} md:flex flex-col flex-1 min-w-0`} style={{ background: "#FAFAF9" }}>
            {selected ? (
              <>
                <button onClick={() => setMobileView("list")} className="md:hidden flex items-center gap-2 px-4 py-3 border-b text-sm font-medium" style={{ color: "#57534E", borderColor: "#E7E5E4" }}>
                  ← Back to conversations
                </button>
                <ThreadConversation key={selected.id} thread={selected} onBack={() => { setSelected(null); setMobileView("list"); }} />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <p className="text-sm font-medium" style={{ color: "#292524" }}>Select a conversation</p>
                <p className="text-xs mt-1" style={{ color: "#A8A29E" }}>or start a new one</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showNew && <NewThreadDialog onClose={() => setShowNew(false)} onCreated={onCreated} />}
    </PortalLayout>
  );
}
